import React, { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

/**
 * Small floating "Back to Top" button for long public pages.
 *
 * - Hidden until the user has scrolled past ~`threshold` px.
 * - Bottom-LEFT placement so we don't fight the bottom-right Emergent badge
 *   on either desktop or mobile.
 * - `aria-hidden` while hidden so it doesn't steal screen-reader focus.
 */
export default function BackToTop({ threshold = 400 }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      data-testid="back-to-top-btn"
      aria-label="Back to top"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      onClick={scrollToTop}
      className={`fixed bottom-6 left-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg ring-1 ring-slate-900/10 transition-all duration-200 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 md:bottom-8 md:left-8 ${
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-2 opacity-0"
      }`}
    >
      <ArrowUp size={18} />
    </button>
  );
}
